<template>
  <div class="left-toolbar flex flex-row gap-[10px]">
    <file-upload
      v-if="showAttachButton"
      ref="uploadAttachment"
      v-tooltip="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
      input-id="conversationAttachment"
      :size="4096 * 4096"
      :accept="allowedAttachments || allowedFileTypes"
      :multiple="enableMultipleFileUpload"
      :drop="enableDragAndDrop"
      :drop-directory="false"
      :data="{
        direct_upload_url: '/rails/active_storage/direct_uploads',
        direct_upload: true,
      }"
      @input-file="file => $emit('input-file', file)"
      @input-filter="
        (newFile, oldFile, prevent) =>
          $emit('input-filter', newFile, oldFile, prevent)
      "
    >
      <span
        :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        class="material-symbols-outlined message-toolbar-item"
        @click="triggerOpenUploadFile"
      >
        attach_file
      </span>
    </file-upload>
    <transition name="modal-fade">
      <div
        v-show="$refs.uploadAttachment && $refs.uploadAttachment.dropActive"
        class="flex items-center justify-center gap-2 fixed left-0 right-0 top-0 bottom-0 w-full h-full z-20 text-slate-600 dark:text-slate-200 bg-white_transparent dark:bg-black_transparent flex-col"
      >
        <fluent-icon icon="cloud-backup" size="40" />
        <h4 class="page-sub-title text-slate-600 dark:text-slate-200">
          {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
        </h4>
      </div>
    </transition>
    <div v-if="showDocalButton" class="flex">
      <span
        v-tooltip="$t('CONVERSATION.REPLYBOX.TIP_DOCAL')"
        class="material-symbols-outlined message-toolbar-item"
        @click="toggleDocal"
      >
        contract_edit
      </span>
      <docal-documents-modal
        :show="showDocalModal"
        :conversation-id="conversationId"
        :inbox-id="inboxId"
        @signing-link="onDocalSigningLink"
        @cancel="showDocalModal = false"
      />
    </div>
    <div v-if="showFormsButton" class="flex">
      <span
        v-tooltip="$t('CONVERSATION.REPLYBOX.TIP_FORMS')"
        class="material-symbols-outlined message-toolbar-item"
        @click="toggleForms"
      >
        assignment
      </span>
      <forms-list-modal
        :show="showFormsModal"
        :conversation-id="conversationId"
        @form-link="onFormLink"
        @cancel="showFormsModal = false"
      />
    </div>
    <div v-if="showEditorToggle">
      <span
        v-tooltip="
          showRichContentEditor
            ? $t('CONVERSATION.REPLYBOX.TIP_SIMPLE_EDITOR')
            : $t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')
        "
        class="material-symbols-outlined message-toolbar-item"
        @click="toggleEditor"
      >
        {{ showRichContentEditor ? 'convert_to_text' : 'article_shortcut' }}
      </span>
    </div>
    <bafi-file-attacher
      v-if="showAttachButton"
      style="margin-inline-start: 1rem"
      :on-file-download="file => $emit('input-file', file)"
    />
    <div v-if="hasWhatsappTemplates">
      <span
        v-tooltip="'Whatsapp Templates'"
        class="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 cursor-pointer"
        @click="$emit('selectWhatsappTemplate')"
      >
        <span
          class="icon-mask w-6 h-6 bg-gray-500"
          :style="{
            '-webkit-mask-image': `url(${whatsappBadge})`,
            'mask-image': `url(${whatsappBadge})`,
          }"
        />
      </span>
    </div>
    <video-call-button
      v-if="(isAWebWidgetInbox || isAPIInbox) && !isPrivateNote"
      :conversation-id="conversationId"
    />
    <AIAssistanceButton
      v-if="showAiAssistance"
      :conversation-id="conversationId"
      :is-private-note="isPrivateNote"
      :message="message"
      @replace-text="$emit('replace-text', $event)"
    />
  </div>
</template>

<script>
import AIAssistanceButton from 'dashboard/components/widgets/AIAssistanceButtonV2.vue';
import ExtendedFileUpload from 'dashboard/components/widgets/WootWriter/ExtendedFileUpload';
import VideoCallButton from 'dashboard/components/widgets/VideoCallButton';

import whatsappBadge from 'dashboard/assets/images/inbox_icons/whatsapp-badge-nocolor.svg';
import inboxMixin from 'shared/mixins/inboxMixin';

const DocalDocumentsModal = () =>
  import('dashboard/components/widgets/conversation/DocalDocumentsModal.vue');

const FormsListModal = () =>
  import('dashboard/components/widgets/conversation/FormsListModal.vue');

const BafiFileAttacher = () =>
  import(
    /* webpackChunkName: "platform_views" */ 'dashboard/components/widgets/online99_platform/BafiFileAttacher.vue'
  );

export default {
  name: 'LeftToolbar',
  components: {
    AIAssistanceButton,
    FileUpload: ExtendedFileUpload,
    DocalDocumentsModal,
    FormsListModal,
    BafiFileAttacher,
    VideoCallButton,
  },
  mixins: [inboxMixin],
  props: {
    showAttachButton: { type: Boolean, default: false },
    allowedAttachments: { type: String, default: undefined },
    allowedFileTypes: { type: [String, Array], default: '' },
    enableMultipleFileUpload: { type: Boolean, default: false },
    enableDragAndDrop: { type: Boolean, default: true },
    showDocalButton: { type: Boolean, default: false },
    showFormsButton: { type: Boolean, default: false },
    conversationId: { type: Number, default: null },
    inboxId: { type: Number, default: null },
    isPrivateNote: { type: Boolean, default: false },
    message: { type: String, default: '' },
    showAiAssistance: { type: Boolean, default: false },
    toggleEditor: { type: Function, default: () => {} },
    showEditorToggle: { type: Boolean, default: false },
    showRichContentEditor: { type: Boolean, default: false },
  },
  data() {
    return {
      showDocalModal: false,
      showFormsModal: false,
      whatsappBadge,
    };
  },
  computed: {
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    hasWhatsappTemplates() {
      return (
        !!this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
          .length &&
        !this.isPrivateNote
      );
    },
  },
  methods: {
    triggerOpenUploadFile() {
      this.$refs.uploadAttachment.$el
        .querySelector('input[type="file"]')
        .click();
    },
    toggleDocal() {
      this.showDocalModal = !this.showDocalModal;
    },
    onDocalSigningLink(url) {
      this.showDocalModal = false;
      this.$emit('signing-link', url);
    },
    toggleForms() {
      this.showFormsModal = !this.showFormsModal;
    },
    onFormLink(url) {
      this.showFormsModal = false;
      this.$emit('form-link', url);
    },
  },
};
</script>
<style lang="scss" scoped>
/* Action icon button (toolbar icons) */
.action-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  transition: background 0.15s ease, color 0.15s ease;

  .material-symbols-outlined {
    font-size: 24px;
    line-height: 1;
    color: #6B7280;
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20;
  }

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  /* AI button */
  &--ai {
    width: 32px;
    height: 32px;
    background: #f0fdf4;
    border: 1px solid #e3ef26;
    border-radius: 4px;
    box-shadow: 0px 1px 8px 0px rgba(221, 221, 221, 0.25);
    color: #15803d;

    &:hover {
      background: #dcfce7;
      color: #166534;
    }
  }
}

::v-deep {
  &.left-toolbar {
    .file-uploads {
      @apply w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center;
    }
    .message-toolbar {
      @apply flex flex-col w-full;
    }
    .message-toolbar-item {
      @apply w-8 h-8 flex items-center justify-center rounded-md cursor-pointer;
      @apply text-[24px] text-[#6B7280] hover:bg-gray-100 p-1 select-none;

      &.active,
      ~ label:hover {
        @apply bg-gray-100;
      }

      &.disabled {
        color: #dfdfdf;
        cursor: not-allowed;
        user-select: none;
      }
    }
  }
}
</style>
